import React, { useCallback, useEffect, useRef, useState } from 'react'
import Loader from 'components/Loader'
import Dialog from 'components/Popup/Dialog'
import { getMessage } from 'lib/translator'
import { MEDIA_AUDIT_LOGS_PAGE_SIZE } from '../constants'
import {
  formatImageSideLabel,
  getMediaAuditErrorMessage,
  queryMediaAuditLogs,
} from '../utils'
import { AUDIT_LOG_TABLE_COLUMNS } from './auditLogFields'
import MediaAuditLogsFilter from './MediaAuditLogsFilter'
import { transformFilter } from './transformFilter'
import { getReadableDateFromISO8601 } from 'lib/datetime'

function formatAuditLogCellValue(column, value) {
  if (value === undefined || value === null || value === '') {
    return '-'
  }

  if (column.formatSide) {
    return formatImageSideLabel(value)
  }

  if (column.formatDateTime) {
    return getReadableDateFromISO8601(value)
  }

  return String(value)
}

function AuditLogsTable({ items }) {
  return (
    <div
      className="sku-media-audit-table-wrap"
      data-testid="sku-media-audit-logs-table-wrap"
    >
      <table className="sku-media-results-table sku-media-audit-logs-table">
        <thead>
          <tr>
            {AUDIT_LOG_TABLE_COLUMNS.map((column) => (
              <th key={column.key}>
                {getMessage(
                  `catalogue.skuMediaUpload.auditLogs.table.${column.key}`
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={item.id ?? `${item.sku}-${item.side}-${index}`}>
              {AUDIT_LOG_TABLE_COLUMNS.map((column) => {
                const value = item[column.key]
                const displayValue = formatAuditLogCellValue(column, value)

                return (
                  <td key={column.key}>
                    {column.isLink && value ? (
                      <a href={value} target="_blank" rel="noopener noreferrer">
                        {displayValue}
                      </a>
                    ) : (
                      displayValue
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function MediaAuditLogs() {
  const [filters, setFilters] = useState({})
  const [filterKey, setFilterKey] = useState(0)
  const [items, setItems] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [errorDialog, setErrorDialog] = useState(null)
  const queryRequestIdRef = useRef(0)

  const pageSize = MEDIA_AUDIT_LOGS_PAGE_SIZE
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))
  const safePage = Math.min(page, pageCount - 1)
  const rangeStart = totalCount === 0 ? 0 : safePage * pageSize + 1
  const rangeEnd = Math.min((safePage + 1) * pageSize, totalCount)

  const loadAuditLogs = useCallback(
    async (nextFilters, nextPage = 0) => {
      const requestId = queryRequestIdRef.current + 1
      queryRequestIdRef.current = requestId
      setLoading(true)
      setErrorDialog(null)

      try {
        const { items: nextItems, totalCount: nextTotalCount } =
          await queryMediaAuditLogs(nextFilters, {
            limit: pageSize,
            offset: nextPage * pageSize,
          })

        if (requestId !== queryRequestIdRef.current) {
          return
        }

        setItems(nextItems)
        setTotalCount(nextTotalCount)
        setPage(nextPage)
        setHasSearched(true)
      } catch (error) {
        if (requestId !== queryRequestIdRef.current) {
          return
        }

        setItems([])
        setTotalCount(0)
        setHasSearched(true)
        setErrorDialog({
          title: getMessage(
            'catalogue.skuMediaUpload.auditLogs.queryError.title'
          ),
          message: getMediaAuditErrorMessage(error),
        })
      } finally {
        if (requestId === queryRequestIdRef.current) {
          setLoading(false)
        }
      }
    },
    [pageSize]
  )

  useEffect(() => {
    loadAuditLogs(filters, 0)
  }, [filters, loadAuditLogs])

  const handleFilterSubmit = (formData) => {
    setFilters(transformFilter(formData))
  }

  const handleFilterClear = () => {
    setFilters({})
    setFilterKey((current) => current + 1)
  }

  return (
    <div className="sku-media-tab sku-media-audit-logs-tab">
      <section
        className="sku-media-audit-logs-search"
        data-testid="sku-media-audit-logs-search"
      >
        <h3>{getMessage('catalogue.skuMediaUpload.auditLogs.listHeading')}</h3>

        <MediaAuditLogsFilter
          key={filterKey}
          onSubmit={handleFilterSubmit}
          onClear={handleFilterClear}
        />
      </section>

      {loading && <Loader />}

      {!loading && hasSearched && items.length === 0 && (
        <p
          className="sku-media-audit-empty"
          data-testid="sku-media-audit-logs-empty"
        >
          {getMessage('catalogue.skuMediaUpload.auditLogs.empty')}
        </p>
      )}

      {!loading && items.length > 0 && (
        <div
          className="sku-media-audit-results"
          data-testid="sku-media-audit-logs-results"
        >
          {totalCount > pageSize && (
            <div
              className="sku-media-preview-pagination"
              data-testid="sku-media-audit-logs-pagination"
            >
              <button
                type="button"
                className="button secondary"
                disabled={safePage === 0 || loading}
                data-testid="sku-media-audit-logs-prev"
                onClick={() => loadAuditLogs(filters, safePage - 1)}
              >
                {getMessage(
                  'catalogue.skuMediaUpload.auditLogs.pagination.previous'
                )}
              </button>
              <span className="sku-media-preview-range">
                {getMessage(
                  'catalogue.skuMediaUpload.auditLogs.pagination.range',
                  {
                    start: rangeStart,
                    end: rangeEnd,
                    total: totalCount,
                  }
                )}
              </span>
              <button
                type="button"
                className="button secondary"
                disabled={safePage >= pageCount - 1 || loading}
                data-testid="sku-media-audit-logs-next"
                onClick={() => loadAuditLogs(filters, safePage + 1)}
              >
                {getMessage(
                  'catalogue.skuMediaUpload.auditLogs.pagination.next'
                )}
              </button>
            </div>
          )}

          <AuditLogsTable items={items} />
        </div>
      )}

      {errorDialog && (
        <Dialog
          className="error"
          show
          title={errorDialog.title}
          information={errorDialog.message}
          close={() => setErrorDialog(null)}
          closeText={getMessage('dialog.okText')}
        />
      )}
    </div>
  )
}

export default MediaAuditLogs
